/**
 * 批量任务记录卡片组件
 * 用于在生图记录页面展示单条批量任务记录
 * 紧凑横向布局，支持下载等操作
 */

import type { ReactElement } from 'react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Clock,
  Loader2,
  CheckCircle,
  XCircle,
  Zap,
  Eye,
  Download,
  Workflow,
  User,
  FileText,
  ChevronDown,
  Timer,
  Grid2x2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import type { BatchTaskListItem } from '@/types/task'
import type { PublicWorkflow } from '@/types/workflow'
import { BATCH_TASK_STATUS, BATCH_TASK_MODE } from '@/types/task'
import { BatchDownloadDialog, DOWNLOAD_MODE } from './BatchDownloadDialog'
import type { DownloadMode } from './BatchDownloadDialog'
import { ImagePackDialog } from './ImagePackDialog'

/**
 * 组件属性
 */
interface BatchRecordCardProps {
  /** 批量任务记录 */
  record: BatchTaskListItem
  /** 工作流列表（用于判断是否多图应用） */
  workflows?: PublicWorkflow[]
  /** 刷新回调（可选） */
  onRefresh?: () => void
}

/**
 * 格式化相对时间
 * @param dateString - ISO 8601 时间字符串
 * @returns 格式化后的相对时间
 */
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSec = Math.floor(diffMs / 1000)
  const diffMin = Math.floor(diffSec / 60)
  const diffHour = Math.floor(diffMin / 60)
  const diffDay = Math.floor(diffHour / 24)

  if (diffSec < 60) {
    return '刚刚'
  }
  if (diffMin < 60) {
    return `${String(diffMin)} 分钟前`
  }
  if (diffHour < 24) {
    return `${String(diffHour)} 小时前`
  }
  if (diffDay < 7) {
    return `${String(diffDay)} 天前`
  }

  return date.toLocaleDateString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * 格式化计划执行时间
 * @param dateString - ISO 8601 时间字符串
 * @returns 格式化后的时间
 */
function formatScheduledTime(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleString('zh-CN', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * 获取状态标签配置
 */
function getStatusConfig(status: string): {
  text: string
  className: string
  icon: typeof CheckCircle
  animate?: boolean
} {
  switch (status) {
    case BATCH_TASK_STATUS.PENDING:
      return {
        text: '等待中',
        className: 'border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400',
        icon: Clock,
      }
    case BATCH_TASK_STATUS.RUNNING:
      return {
        text: '处理中',
        className: 'border-blue-500/30 bg-blue-500/10 text-blue-600 dark:text-blue-400',
        icon: Loader2,
        animate: true,
      }
    case BATCH_TASK_STATUS.COMPLETED:
      return {
        text: '已完成',
        className: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
        icon: CheckCircle,
      }
    case BATCH_TASK_STATUS.PARTIAL_FAILED:
      return {
        text: '部分失败',
        className: 'border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400',
        icon: XCircle,
      }
    case BATCH_TASK_STATUS.FAILED:
      return {
        text: '失败',
        className: 'border-red-500/30 bg-red-500/10 text-red-600 dark:text-red-400',
        icon: XCircle,
      }
    case BATCH_TASK_STATUS.PAUSED:
      return {
        text: '已暂停',
        className: 'border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400',
        icon: Clock,
      }
    case BATCH_TASK_STATUS.CANCELLED:
      return {
        text: '已取消',
        className: 'border-muted-foreground/30 bg-muted text-muted-foreground',
        icon: XCircle,
      }
    default:
      return {
        text: '未知',
        className: 'border-muted-foreground/30 bg-muted text-muted-foreground',
        icon: Clock,
      }
  }
}

/**
 * 迷你进度条组件
 */
interface MiniProgressBarProps {
  completed: number
  failed: number
  cancelled: number
  total: number
}

function MiniProgressBar({ completed, failed, cancelled, total }: MiniProgressBarProps): ReactElement {
  const completedPercent = total > 0 ? (completed / total) * 100 : 0
  const failedPercent = total > 0 ? (failed / total) * 100 : 0
  const cancelledPercent = total > 0 ? (cancelled / total) * 100 : 0

  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
      <div className="flex h-full">
        <motion.div
          className="h-full bg-emerald-500"
          initial={{ width: 0 }}
          animate={{ width: `${String(completedPercent)}%` }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        />
        <motion.div
          className="h-full bg-red-500"
          initial={{ width: 0 }}
          animate={{ width: `${String(failedPercent)}%` }}
          transition={{ duration: 0.5, ease: 'easeOut', delay: 0.1 }}
        />
        <motion.div
          className="h-full bg-slate-400 dark:bg-slate-500"
          initial={{ width: 0 }}
          animate={{ width: `${String(cancelledPercent)}%` }}
          transition={{ duration: 0.5, ease: 'easeOut', delay: 0.2 }}
        />
      </div>
    </div>
  )
}

/**
 * 统计数字组件
 */
interface StatItemProps {
  icon: typeof CheckCircle
  value: number
  label: string
  colorClass: string
  animate?: boolean
}

function StatItem({ icon: iconComponent, value, label, colorClass, animate }: StatItemProps): ReactElement {
  const IconComponent = iconComponent
  return (
    <div className="flex items-center gap-1">
      <IconComponent className={cn('h-3.5 w-3.5', colorClass, animate === true && 'animate-spin')} />
      <span className={cn('text-xs font-medium', colorClass)}>{value}</span>
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  )
}

/**
 * 批量任务记录卡片组件
 * @param props - 组件属性
 * @returns 记录卡片 ReactElement
 */
export function BatchRecordCard({
  record,
}: BatchRecordCardProps): ReactElement {
  const statusConfig = getStatusConfig(record.status)
  const StatusIcon = statusConfig.icon

  // 下载弹窗状态
  const [showDownloadDialog, setShowDownloadDialog] = useState(false)
  const [downloadMode, setDownloadMode] = useState<DownloadMode>(DOWNLOAD_MODE.IMAGES)

  // ZIP打包弹窗状态
  const [showPackDialog, setShowPackDialog] = useState(false)

  // 判断是否已结束（用于进度条展示）
  const isFinished =
    record.status === BATCH_TASK_STATUS.COMPLETED ||
    record.status === BATCH_TASK_STATUS.PARTIAL_FAILED ||
    record.status === BATCH_TASK_STATUS.FAILED

  // 是否有可下载的结果（允许任务仍在排队/处理中时下载已完成部分）
  const hasDownloadableResults = record.completed_count > 0

  // 获取排队中和运行中的数量
  const queuedCount = record.queued_count
  const runningCount = record.running_count

  // 是否仍有子任务未完成
  const hasInProgressTasks = queuedCount > 0 || runningCount > 0

  // 是否为定时任务
  const isScheduled = record.task_mode === BATCH_TASK_MODE.SCHEDULED

  const isTextToImage = record.generation_source_type === 'text_to_image'
  const showRepeatBadge = record.repeat_count > 1
  const sourceSummary = isTextToImage
    ? `${String(record.source_count)} 条提示词`
    : `${String(record.source_count)} 张原图`

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className={cn(
        'group rounded-xl border bg-card px-4 py-3 shadow-sm transition-all',
        'hover:border-primary/40 hover:shadow-md'
      )}
    >
      {/* 主内容区 - 水平布局 */}
      <div className="flex items-center gap-4">
        {/* 左侧：任务信息 */}
        <div className="min-w-0 flex-1">
          {/* 第一行：备注（重点内容） + 定时标签 + 状态标签 */}
          <div className="flex items-center gap-2">
            <h3 className="truncate text-base font-semibold">
              {record.remark !== '' ? record.remark : '未命名任务'}
            </h3>
            {/* 定时任务标签 */}
            {isScheduled && (
              <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full border border-blue-500/30 bg-blue-500/10 px-1.5 py-0.5 text-[10px] font-medium text-blue-600 dark:text-blue-400">
                <Timer className="h-2.5 w-2.5" />
                定时
              </span>
            )}
            {isTextToImage && (
              <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-medium text-emerald-600 dark:text-emerald-400">
                <FileText className="h-2.5 w-2.5" />
                文生图
              </span>
            )}
            {showRepeatBadge && (
              <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full border border-primary/30 bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
                <Grid2x2 className="h-2.5 w-2.5" />
                每组 x{record.repeat_count}
              </span>
            )}
            {/* 状态标签 */}
            <span
              className={cn(
                'inline-flex shrink-0 items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-medium',
                statusConfig.className
              )}
            >
              <StatusIcon
                className={cn('h-3 w-3', statusConfig.animate === true && 'animate-spin')}
              />
              {statusConfig.text}
            </span>
          </div>
          {/* 第二行：工作流名称 + 提交者（子账号时显示）+ 计划时间 + 任务ID */}
          <div className="mt-1 flex items-center gap-3 text-xs">
            <span className="flex items-center gap-1 text-muted-foreground">
              <Workflow className="h-3 w-3" />
              <span>{record.workflow_name || `工作流 ${record.workflow_id.slice(0, 8)}`}</span>
            </span>
            <span className="text-muted-foreground">
              {sourceSummary} / 共 {record.total_count} 张
            </span>
            {/* 提交者信息：仅当有操作者用户名时显示 */}
            {record.operator_username !== undefined && record.operator_username !== '' && (
              <span className="flex items-center gap-1 text-muted-foreground">
                <User className="h-3 w-3" />
                <span>
                  {(record.is_sub_account ?? false) ? '子账号: ' : ''}
                  {record.operator_username}
                </span>
              </span>
            )}
            {/* 计划执行时间（仅定时任务显示） */}
            {isScheduled && record.scheduled_at !== undefined && (
              <span className="flex items-center gap-1 text-blue-600 dark:text-blue-400">
                计划: {formatScheduledTime(record.scheduled_at)}
              </span>
            )}
            <span className="text-muted-foreground/50">
              ID: {record.id.slice(0, 8)}
            </span>
          </div>
        </div>

        {/* 中间：统计信息 */}
        <div className="hidden shrink-0 items-center gap-4 sm:flex">
          {/* 成功/失败/排队中/运行中统计 */}
          <div className="flex items-center gap-3">
            {record.completed_count > 0 && (
              <StatItem
                icon={CheckCircle}
                value={record.completed_count}
                label="成功"
                colorClass="text-emerald-600 dark:text-emerald-400"
              />
            )}
            {record.failed_count > 0 && (
              <StatItem
                icon={XCircle}
                value={record.failed_count}
                label="失败"
                colorClass="text-red-600 dark:text-red-400"
              />
            )}
            {record.cancelled_count !== undefined && record.cancelled_count > 0 && (
              <StatItem
                icon={XCircle}
                value={record.cancelled_count}
                label="已取消"
                colorClass="text-slate-500 dark:text-slate-400"
              />
            )}
            {runningCount > 0 && (
              <StatItem
                icon={Loader2}
                value={runningCount}
                label="运行中"
                colorClass="text-blue-600 dark:text-blue-400"
                animate
              />
            )}
            {queuedCount > 0 && (
              <StatItem
                icon={Clock}
                value={queuedCount}
                label="排队中"
                colorClass="text-amber-600 dark:text-amber-400"
              />
            )}
          </div>

          {/* 分隔线 */}
          <div className="h-6 w-px bg-border" />

          {/* 算力消耗 */}
          <div className="flex items-center gap-1 text-xs">
            <Zap className="h-3.5 w-3.5 text-amber-500" />
            <span className="font-medium">{record.consumed_power}</span>
            <span className="text-muted-foreground">算力</span>
          </div>

          {/* 时间 */}
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="h-3.5 w-3.5" />
            <span>{formatRelativeTime(record.created_at)}</span>
          </div>
        </div>

        {/* 右侧：操作按钮 */}
        <div className="flex shrink-0 items-center gap-1">
          {/* 下载按钮（有成功图片时显示，允许未完全结束的任务下载已完成部分） */}
          {hasDownloadableResults && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 gap-1.5 px-2.5 text-xs"
                >
                  <Download className="h-3.5 w-3.5" />
                  <span className="hidden lg:inline">下载</span>
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={(): void => {
                    setDownloadMode(DOWNLOAD_MODE.URLS)
                    setShowDownloadDialog(true)
                  }}
                >
                  <FileText className="mr-2 h-4 w-4" />
                  {hasInProgressTasks ? '导出已完成图片地址' : '导出图片地址'}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(): void => {
                    setShowPackDialog(true)
                  }}
                >
                  <Download className="mr-2 h-4 w-4" />
                  {hasInProgressTasks ? '打包已完成图片' : '打包ZIP下载'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* 查看详情按钮 */}
          <Button asChild variant="outline" size="sm" className="h-8 gap-1.5 px-2.5 text-xs">
            <Link to={`/records/generations/${record.id}`}>
              <Eye className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">详情</span>
            </Link>
          </Button>
        </div>
      </div>

      {/* 底部：移动端统计 + 进度条 */}
      <div className="mt-2 sm:hidden">
        {/* 移动端统计 */}
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-3">
            {record.completed_count > 0 && (
              <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                <CheckCircle className="h-3 w-3" />
                {record.completed_count}成功
              </span>
            )}
            {record.failed_count > 0 && (
              <span className="flex items-center gap-1 text-red-600 dark:text-red-400">
                <XCircle className="h-3 w-3" />
                {record.failed_count}失败
              </span>
            )}
            {record.cancelled_count !== undefined && record.cancelled_count > 0 && (
              <span className="flex items-center gap-1 text-slate-500 dark:text-slate-400">
                <XCircle className="h-3 w-3" />
                {record.cancelled_count}已取消
              </span>
            )}
            {runningCount > 0 && (
              <span className="flex items-center gap-1 text-blue-600 dark:text-blue-400">
                <Loader2 className="h-3 w-3 animate-spin" />
                {runningCount}运行中
              </span>
            )}
            {queuedCount > 0 && (
              <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                <Clock className="h-3 w-3" />
                {queuedCount}排队中
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <span className="flex items-center gap-1">
              <Zap className="h-3 w-3 text-amber-500" />
              {record.consumed_power}
            </span>
            <span>{formatRelativeTime(record.created_at)}</span>
          </div>
        </div>
      </div>

      {/* 进度条（处理中时显示） */}
      {!isFinished && (
        <div className="mt-2">
          <MiniProgressBar
            completed={record.completed_count}
            failed={record.failed_count}
            cancelled={record.cancelled_count ?? 0}
            total={record.total_count}
          />
        </div>
      )}

      {/* 批量下载弹窗 */}
      <BatchDownloadDialog
        open={showDownloadDialog}
        onOpenChange={setShowDownloadDialog}
        batchId={record.id}
        remark={record.remark}
        expectedCount={record.completed_count}
        mode={downloadMode}
      />

      {/* ZIP打包弹窗 */}
      <ImagePackDialog
        open={showPackDialog}
        onOpenChange={setShowPackDialog}
        batchId={record.id}
        remark={record.remark}
        totalCount={record.completed_count}
      />

    </motion.div>
  )
}
